import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { isBeamterAbrechnungsgruppe } from '../../users/employees/entities/employee-sections/an-beschaeftigung.js';
import { Person } from '../../users/entities/person.js';
import { isContractActive } from '../api/contractTimeframes.js';
import { Contract } from './Contract.js';
import { ContractSupplementChange } from './ContractSupplement.js';

@Entity()
export class ContractSupplementApplyHistoryForPath extends AdornisEntity {
  static override _class = 'ContractSupplementApplyHistoryForPath';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(() => String)
  public supplementID: Maybe<string>;

  @Field(() => [ContractSupplementChange], { default: v => v ?? [] })
  public changes: Maybe<ContractSupplementChange[]>;

  @validate(nonOptional())
  @Field(() => String)
  public path: Maybe<string>;
}

@Entity()
export class ContractSupplementApplyHistory extends AdornisEntity {
  static override _class = 'ContractSupplementApplyHistory';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(() => [ContractSupplementApplyHistoryForPath], { default: v => v ?? [] })
  public paths: Maybe<ContractSupplementApplyHistoryForPath[]>;
}

@Entity()
export class AppliedContract extends Contract {
  public static override _class = 'AppliedContract';
  public static override _collectionName = 'do-not-save-to-db';

  @Field(type => Date, { default: v => v ?? new Date() })
  appliedDate!: Date;

  // If the user selects a date after the Kündigung, the contract will set its applied Date to the Kündigungsdate to not loose any Info from supplements.
  // In a sense the user will always see the last active state of the contract.
  // As the applied date is now set to kündigungsdate we want to save the original time the user selected for resolvers like active, which want to use the original set date
  @Field(type => Date)
  forcedUserAppliedDate: Maybe<Date>;

  @Field(type => String)
  lastAppliedSupplement!: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  includesDrafts!: boolean;

  @Field(type => Date)
  appliedSupplementStartAG1: Maybe<Date>;

  @Field(type => Date)
  appliedSupplementEndAG1: Maybe<Date>;

  @Field(type => Date)
  appliedSupplementStartAG2: Maybe<Date>;

  @Field(type => Date)
  appliedSupplementEndAG2: Maybe<Date>;

  @Dependencies({
    classes: () => [],
    ids: () => [],
  })
  @Field(() => Boolean, {
    resolve: function (this: AppliedContract) {
      return async () => {
        // a filter always loads a list, the _id matches at most one person
        const [person] = await loadMongoData<typeof Person>(
          Person,
          { _id: this.userID },
          {
            projection: {
              _id: 1,
              mitarbeiterDaten: {
                anBeschaeftigung: {
                  abrechnungsgruppe: 1,
                },
              },
            },
          },
        );

        // isContractActive normalizes to the calendar day itself — no end-of-day/-1h gymnastics needed
        const res = isContractActive(
          this.forcedUserAppliedDate ?? this.appliedDate,
          this,
          isBeamterAbrechnungsgruppe(person?.mitarbeiterDaten?.anBeschaeftigung?.abrechnungsgruppe),
        );
        return res;
      };
    },
  })
  active: Maybe<boolean>;

  @Field(type => ContractSupplementApplyHistory, { default: v => v ?? new ContractSupplementApplyHistory({}) })
  history: Maybe<ContractSupplementApplyHistory>;

  override async save(): Promise<number> {
    throw new Error('You cannot save an applied contract. Use the client function "saveAppliedContract" instead.');
  }

  override async remove(): Promise<boolean> {
    throw new Error('You cannot remove an applied contract.');
  }

  override create(): Promise<string> {
    throw new Error('You cannot create an applied contract.');
  }
}
